import Fuse from "fuse.js";
import { getAllGuides } from "./loadGuides";
import { faqEntries } from "../../pages/help/content/faq";
import type { FaqEntry, GuideFrontmatter } from "./types";

export interface SearchResult {
  kind: "guide" | "faq";
  title: string;
  description: string;
  href: string;
  category: string;
}

function guideToResult(fm: GuideFrontmatter): SearchResult {
  return {
    kind: "guide",
    title: fm.title,
    description: fm.description,
    href: `/help/guides/${fm.slug}`,
    category: fm.category,
  };
}

function faqToResult(entry: FaqEntry): SearchResult {
  return {
    kind: "faq",
    title: entry.question,
    description: entry.answer.slice(0, 140),
    href: `/help/faq#${entry.id}`,
    category: entry.category,
  };
}

interface IndexedItem extends SearchResult {
  tags: string[];
  body: string;
}

let cached: Fuse<IndexedItem> | null = null;

export function getSearchIndex(): Fuse<IndexedItem> {
  if (cached) return cached;

  const items: IndexedItem[] = [
    ...getAllGuides().map((g) => ({
      ...guideToResult(g.frontmatter),
      tags: g.frontmatter.tags,
      body: g.frontmatter.description,
    })),
    ...faqEntries.map((entry) => ({
      ...faqToResult(entry),
      tags: entry.tags,
      body: entry.answer,
    })),
  ];

  cached = new Fuse(items, {
    keys: [
      { name: "title", weight: 3 },
      { name: "description", weight: 1.5 },
      { name: "tags", weight: 2 },
      { name: "body", weight: 1 },
    ],
    threshold: 0.35,
    ignoreLocation: true,
    minMatchCharLength: 2,
  });

  return cached;
}

export function searchHelp(query: string, limit = 8): SearchResult[] {
  const q = query.trim();
  if (q.length < 2) return [];
  const index = getSearchIndex();
  return index
    .search(q, { limit })
    .map(({ item }) => ({
      kind: item.kind,
      title: item.title,
      description: item.description,
      href: item.href,
      category: item.category,
    }));
}
